import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X, Fuel, ClipboardCheck, Camera, AlertTriangle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

import { useTranslation } from "react-i18next";
const STORAGE_KEY = "hisabx_csa_tutorial_seen_v1";

const SLIDES = [
  {
    icon: Fuel,
    title: "Welcome to HisabX",
    body: "The paper HISAB book is now on your phone. In 5 quick steps, here's how a shift works — same as before, just faster and fewer mistakes.",
    tone: "primary",
  },
  {
    icon: Fuel,
    title: "1 · Start your shift",
    body: "Tap Start shift. Pick the nozzles you'll be operating from the list. Your opening reading is filled in automatically from the previous CSA's closing.",
    tone: "info",
  },
  {
    icon: ClipboardCheck,
    title: "2 · During the shift — log online payments & credit",
    body: "When someone pays via UPI or on credit, tap + and record it. Don't wait till end of shift — do it as it happens. Cash you keep in hand, no need to log each rupee.",
    tone: "info",
  },
  {
    icon: Camera,
    title: "3 · Close your shift — snap the printer slip",
    body: "At end of shift, tap 📸 Scan slip. Take a clear photo of the printer receipt. If there's no printer, tap ⌨️ Enter manually. Your closing reading gets saved and auto-fills for the next CSA.",
    tone: "info",
  },
  {
    icon: AlertTriangle,
    title: "4 · Submit HISAB & wait for verify",
    body: "Enter cash you're handing over. Submit. Manager will Approve or Reject with a reason. Rejected? You'll see a red banner on your home — tap Fix now, edit, resubmit. That's it.",
    tone: "warn",
  },
];

/**
 * CSA onboarding tutorial — 5-slide walkthrough shown on first login.
 * Dismissable + persisted per-user in localStorage.
 */
export default function CsaOnboardingTutorial({ forceShow = false, onDone }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (forceShow) { setVisible(true); return; }
    if (typeof localStorage !== "undefined" && !localStorage.getItem(STORAGE_KEY)) {
      setVisible(true);
    }
  }, [forceShow]);

  const close = () => {
    if (typeof localStorage !== "undefined") localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
    onDone?.();
  };

  if (!visible) return null;
  const slide = SLIDES[idx];
  const Icon = slide.icon;
  const isLast = idx === SLIDES.length - 1;

  const toneClasses = {
    primary: "from-amber-500 to-orange-500",
    info: "from-blue-500 to-cyan-500",
    warn: "from-red-500 to-pink-500",
  }[slide.tone] || "from-primary to-primary";

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-background/85 backdrop-blur-sm p-2 sm:items-center sm:p-4" data-testid="csa-tutorial">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        {/* Hero */}
        <div className={`bg-gradient-to-br ${toneClasses} p-5 text-white`}>
          <div className="flex items-start justify-between">
            <div className="rounded-full bg-white/20 p-2 backdrop-blur-sm">
              <Icon className="h-6 w-6" />
            </div>
            <button onClick={close} className="rounded p-1 hover:bg-white/20" data-testid="tutorial-close" aria-label={t("common.close")}>
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-4 text-[10px] font-bold uppercase tracking-widest opacity-80">
            {idx === 0 ? <><Sparkles className="mr-1 inline h-3 w-3" /> Quick tour</> : `Step ${idx} of ${SLIDES.length - 1}`}
          </div>
          <h2 className="mt-1 text-xl font-bold" data-testid="tutorial-title">{slide.title}</h2>
        </div>
        <div className="px-5 py-4">
          <p className="text-[13px] leading-relaxed text-foreground/85" data-testid="tutorial-body">{slide.body}</p>
        </div>
        {/* Nav */}
        <div className="flex items-center justify-between border-t border-border bg-secondary/30 px-3 py-2">
          <Button variant="ghost" size="sm" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="tutorial-prev">
            <ChevronLeft className="h-4 w-4" /> Back
          </Button>
          <div className="flex gap-1">
            {SLIDES.map((_, i) => (
              <div key={i} className={"h-1.5 w-6 rounded-full " + (i === idx ? "bg-primary" : "bg-secondary")} />
            ))}
          </div>
          {isLast ? (
            <Button size="sm" onClick={close} data-testid="tutorial-done">{t("components.csaonboardingtutorial.let_s_go")}</Button>
          ) : (
            <Button size="sm" onClick={() => setIdx(idx + 1)} data-testid="tutorial-next">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
